'use client';

import { useEffect, useRef, useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import { getBookingUrl } from '@/lib/booking';
import { useLocale } from '@/context/LocaleContext';
import { getPublicStrings } from '@/i18n/public';

export default function PricingSection() {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);
  const locale = useLocale();
  const copy = getPublicStrings(locale).home.pricing;
  const privateFrom = process.env.NEXT_PUBLIC_PRICE_PRIVATE_FROM?.trim();
  const groupFrom = process.env.NEXT_PUBLIC_PRICE_GROUP_FROM?.trim();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }
    return () => observer.disconnect();
  }, []);

  const rows = [
    {
      title: copy.rows[0].title,
      hint: privateFrom ? `${copy.fromPrefix} ${privateFrom}` : copy.privateFallback,
      note: copy.rows[0].note,
      cta: copy.rows[0].cta,
    },
    {
      title: copy.rows[1].title,
      hint: groupFrom ? `${copy.fromPrefix} ${groupFrom}` : copy.groupFallback,
      note: copy.rows[1].note,
      cta: copy.rows[1].cta,
    },
    {
      title: copy.rows[2].title,
      hint: copy.rows[2].hint,
      note: copy.rows[2].note,
      cta: copy.rows[2].cta,
    },
  ];

  return (
    <section
      id="pricing"
      ref={sectionRef}
      className="border-b border-border bg-white px-6 py-16 md:px-12 md:py-24"
    >
      <div className="mx-auto max-w-7xl">
        <div
          className={`mx-auto mb-12 max-w-3xl text-center transition-all duration-800 ${
            isVisible ? 'fade-in-up' : 'fade-in-up-init'
          }`}
        >
          <span className="text-[10px] font-black uppercase tracking-[0.3em] text-gold">
            {copy.eyebrow}
          </span>
          <h2 className="mt-4 font-heading text-3xl font-bold text-foreground md:text-5xl">
            {copy.title} <span className="italic text-gold">{copy.titleItalic}</span>
          </h2>
          <p className="mt-4 text-base font-light leading-relaxed text-foreground/70">
            {copy.intro}
          </p>
        </div>

        <div className="grid gap-6 md:grid-cols-3">
          {rows.map((row, index) => (
            <div
              key={row.title}
              className={`rounded-2xl border border-border bg-background p-8 shadow-sm transition-all duration-500 ${
                isVisible ? 'fade-in-up' : 'fade-in-up-init'
              }`}
              style={{ animationDelay: `${200 + index * 80}ms` }}
            >
              <h3 className="font-heading text-xl font-bold text-primary">{row.title}</h3>
              <p className="mt-2 text-sm font-semibold text-gold">{row.hint}</p>
              <p className="mt-3 text-sm leading-relaxed text-foreground/75">{row.note}</p>
              <a
                href={getBookingUrl(`pricing_${index}`)}
                className="mt-6 inline-flex items-center gap-2 text-xs font-black uppercase tracking-widest text-primary hover:text-gold"
              >
                {row.cta}
                <Icon name="ArrowRightIcon" size={14} />
              </a>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
